/**
 * scaffold.ts — Pro-specific scaffold mutation helpers
 *
 * Extracted from src/index.ts so these pure filesystem transforms can be
 * imported and tested independently (no __dirname, no fs.copy, no chalk).
 *
 * Every function here:
 *   - Accepts a targetDir and operates on real files
 *   - Is async/await throughout
 *   - Has no side effects beyond writing to targetDir
 *   - Is fully unit-testable with a real temp filesystem
 */

import fs from 'fs-extra';
import yaml from 'js-yaml';
import path from 'path';
import { PRO_VERSIONS, OSS_VERSIONS } from './generated/workspace-versions.js';

// ---------------------------------------------------------------------------
// Package.json mutations
// ---------------------------------------------------------------------------

/**
 * Inject Pro runtime dependencies and scripts into an existing package.json.
 * Enforces OSS version floors and adds Pro-specific dev scripts (prebuild/predev).
 * Safe to call multiple times — idempotent for dep keys.
 */
export async function addProDepsToPackageJson(targetDir: string): Promise<void> {
  const pkgPath = path.join(targetDir, 'package.json');
  const content = await fs.readFile(pkgPath, 'utf-8');
  const pkg = JSON.parse(content) as Record<string, unknown>;

  const dependencies: Record<string, string> =
    typeof pkg.dependencies === 'object' && pkg.dependencies !== null
      ? { ...(pkg.dependencies as Record<string, string>) }
      : {};
  const devDependencies: Record<string, string> =
    typeof pkg.devDependencies === 'object' && pkg.devDependencies !== null
      ? { ...(pkg.devDependencies as Record<string, string>) }
      : {};
  const scripts: Record<string, string> =
    typeof pkg.scripts === 'object' && pkg.scripts !== null
      ? { ...(pkg.scripts as Record<string, string>) }
      : {};

  // Enforce OSS version floors — replace workspace:* refs AND upgrade any stale version
  // pinned by the OSS scaffold template to our tested minimum.
  // OSS_VERSIONS = "minimum tested versions" — we always want them to win.
  for (const [pkgName, pinnedVersion] of Object.entries(OSS_VERSIONS)) {
    if (pkgName in dependencies) {
      dependencies[pkgName] = pinnedVersion;
    }
    if (pkgName in devDependencies) {
      devDependencies[pkgName] = pinnedVersion;
    }
  }

  // Pro runtime dependencies — exact versions from workspace at build time
  Object.assign(dependencies, {
    '@stackwright-pro/auth': PRO_VERSIONS['@stackwright-pro/auth'],
    '@stackwright-pro/auth-nextjs': PRO_VERSIONS['@stackwright-pro/auth-nextjs'],
    '@stackwright-pro/build-scripts-plugins':
      PRO_VERSIONS['@stackwright-pro/build-scripts-plugins'],
    // Cesium is the Pro default map provider (PR #368). The providers.tsx emitter
    // unconditionally imports @stackwright-pro/cesium — both it and its peer dep
    // must be present or next dev blows up on every fresh scaffold.
    '@stackwright-pro/cesium': PRO_VERSIONS['@stackwright-pro/cesium'],
    cesium: '^1.122.0',
    '@stackwright-pro/display-components': PRO_VERSIONS['@stackwright-pro/display-components'],
    '@stackwright-pro/mcp': PRO_VERSIONS['@stackwright-pro/mcp'],
    '@stackwright-pro/openapi': PRO_VERSIONS['@stackwright-pro/openapi'],
    '@stackwright-pro/otters': PRO_VERSIONS['@stackwright-pro/otters'],
    '@stackwright-pro/pulse': PRO_VERSIONS['@stackwright-pro/pulse'],
    '@stackwright-pro/workflow': PRO_VERSIONS['@stackwright-pro/workflow'],
    '@stackwright-pro/workflow-components': PRO_VERSIONS['@stackwright-pro/workflow-components'],
    '@stackwright/build-scripts': OSS_VERSIONS['@stackwright/build-scripts'],
    zod: '^4.0.0',
  });

  // Pro dev dependencies
  Object.assign(devDependencies, {
    '@stoplight/prism-cli': '^5.15.10',
    concurrently: '^9.0.0',
  });

  // Role-based dev scripts (placeholders; auth tool rewrites these per-role)
  Object.assign(scripts, {
    mock: 'node scripts/start-mocks.js',
    'dev:admin':
      'concurrently "pnpm mock" "NEXT_PUBLIC_MOCK_BACKEND=true MOCK_USER=admin next dev"',
    'dev:analyst':
      'concurrently "pnpm mock" "NEXT_PUBLIC_MOCK_BACKEND=true MOCK_USER=analyst next dev"',
    'dev:viewer':
      'concurrently "pnpm mock" "NEXT_PUBLIC_MOCK_BACKEND=true MOCK_USER=viewer next dev"',
    prebuild: 'node scripts/prebuild.js',
    predev: 'node scripts/prebuild.js',
  });

  pkg.dependencies = dependencies;
  pkg.devDependencies = devDependencies;
  pkg.scripts = scripts;

  await fs.writeFile(pkgPath, JSON.stringify(pkg, null, 2) + '\n', 'utf-8');
}

// ---------------------------------------------------------------------------
// stackwright.yml mutations
// ---------------------------------------------------------------------------

/**
 * Add an empty `auth:` key to an existing stackwright.yml.
 * The auth key is the hook point for Pro RBAC configuration.
 */
export async function addAuthToStackwrightYml(targetDir: string): Promise<void> {
  const ymlPath = path.join(targetDir, 'stackwright.yml');
  const content = await fs.readFile(ymlPath, 'utf-8');
  const config = yaml.load(content, { schema: yaml.CORE_SCHEMA }) as Record<string, unknown>;

  config.auth = {};

  await fs.writeFile(ymlPath, yaml.dump(config, { lineWidth: 120 }));
}

/**
 * Append an OpenAPI integration entry to the `integrations:` array in stackwright.yml.
 * Creates the array if it does not exist. Safe to call multiple times for different specs.
 */
export async function addIntegrationToStackwrightYml(
  targetDir: string,
  specName: string,
  specFilename: string,
  mockUrl?: string
): Promise<void> {
  const ymlPath = path.join(targetDir, 'stackwright.yml');
  const content = await fs.readFile(ymlPath, 'utf-8');
  const config = yaml.load(content, { schema: yaml.CORE_SCHEMA }) as Record<string, unknown>;

  const integration: Record<string, unknown> = {
    type: 'openapi',
    name: specName,
    spec: `./specs/${specFilename}`,
    collections: [],
  };

  if (mockUrl) {
    integration.mockUrl = mockUrl;
  }

  config.integrations = [
    ...(Array.isArray(config.integrations) ? config.integrations : []),
    integration,
  ];

  await fs.writeFile(ymlPath, yaml.dump(config, { lineWidth: 120 }));
}

// ---------------------------------------------------------------------------
// .code_puppy/ workspace config scaffolding
// ---------------------------------------------------------------------------

/**
 * Create (or idempotently update) the .code_puppy/ workspace directory.
 *
 * Creates:
 *  - .code_puppy/config.json          { projectOnly: true } (merges with existing)
 *  - .code_puppy/mcp_servers.json     stackwright-pro-mcp stdio server entry
 *  - .code_puppy/agents/              empty directory for future agent configs
 *
 * Symlink guard: refuses to write if any of the managed paths is a symlink,
 * consistent with raft/src/lib.ts ensureWorkspaceConfig.
 */
export async function scaffoldWorkspaceConfig(targetDir: string): Promise<void> {
  const workspaceDir = path.join(targetDir, '.code_puppy');
  await fs.ensureDir(workspaceDir);
  // Symlink guard — consistent with raft/src/lib.ts ensureWorkspaceConfig
  const workspaceStats = await fs.lstat(workspaceDir);
  if (workspaceStats.isSymbolicLink()) {
    throw new Error('.code_puppy/ is a symlink — refusing to write. Check for tampering.');
  }

  const agentsDir = path.join(workspaceDir, 'agents');
  await fs.ensureDir(agentsDir);
  if (await fs.pathExists(agentsDir)) {
    const agentsStats = await fs.lstat(agentsDir);
    if (agentsStats.isSymbolicLink()) {
      throw new Error('.code_puppy/agents is a symlink — refusing to write. Check for tampering.');
    }
  }

  // config.json — projectOnly: true
  // Merge with existing so we don't clobber user customizations on re-scaffold
  const configPath = path.join(workspaceDir, 'config.json');
  let existingConfig: Record<string, unknown> = {};
  if (fs.existsSync(configPath)) {
    try {
      existingConfig = JSON.parse(await fs.readFile(configPath, 'utf-8')) as Record<
        string,
        unknown
      >;
    } catch {
      // Malformed — start fresh
    }
  }
  if (await fs.pathExists(configPath)) {
    const configStats = await fs.lstat(configPath);
    if (configStats.isSymbolicLink()) {
      throw new Error(
        '.code_puppy/config.json is a symlink — refusing to write. Check for tampering.'
      );
    }
  }
  await fs.writeFile(
    configPath,
    JSON.stringify({ ...existingConfig, projectOnly: true }, null, 2) + '\n',
    'utf-8'
  );

  // mcp_servers.json — pnpm-based stackwright-pro-mcp server
  // ${PROJECT_ROOT} is a literal — raft-puppy expands it at runtime to the
  // directory containing .code_puppy/ (walk-up discovery root).
  const mcpServersPath = path.join(workspaceDir, 'mcp_servers.json');
  if (await fs.pathExists(mcpServersPath)) {
    const mcpStats = await fs.lstat(mcpServersPath);
    if (mcpStats.isSymbolicLink()) {
      throw new Error(
        '.code_puppy/mcp_servers.json is a symlink — refusing to write. Check for tampering.'
      );
    }
  }
  if (!fs.existsSync(mcpServersPath)) {
    const mcpConfig = {
      mcp_servers: {
        'stackwright-pro-mcp': {
          type: 'stdio',
          command: 'pnpm',
          args: ['exec', 'stackwright-pro-mcp'],
          enabled: true,
          cwd: '${PROJECT_ROOT}',
        },
      },
    };
    await fs.writeFile(mcpServersPath, JSON.stringify(mcpConfig, null, 2) + '\n', 'utf-8');
  }
}

// ---------------------------------------------------------------------------
// README generation
// ---------------------------------------------------------------------------

export interface GenerateReadmeOptions {
  projectName: string;
  hasSpec: boolean;
  specName?: string;
  specFilename?: string;
  hasOtters: boolean;
  hasMock: boolean;
}

/**
 * Generate a project README.md with conditional sections based on which
 * Pro features are enabled (spec, mock server, otter raft).
 */
export function generateReadme(options: GenerateReadmeOptions): string {
  const { projectName, hasSpec, specName, specFilename, hasOtters, hasMock } = options;

  // Build API Integration section if spec is present
  const apiIntegrationSection = hasSpec
    ? `## 📡 API Integration\n\nYour OpenAPI spec was copied to \`specs/${specFilename}\` and wired up in \`stackwright.yml\`.\n\n**What gets generated:**\n\n\`\`\`typescript\n// src/generated/${specName}/schemas.ts\nexport const EquipmentSchema = z.object({\n  id: z.string(),\n  name: z.string(),\n  status: z.enum(['operational', 'maintenance', 'retired']),\n  // ... Zod validation for runtime safety\n});\n\n// src/generated/${specName}/types.ts\nexport type Equipment = z.infer<typeof EquipmentSchema>;\n\n// src/generated/${specName}/client.ts\nexport class ${(specName ?? 'api').charAt(0).toUpperCase() + (specName ?? 'api').slice(1)}Client {\n  async getEquipment(id: string): Promise<Equipment> {\n    // Auto-wired with auth, validation, error handling\n  }\n}\n\`\`\`\n\n**How it works:**\n1. The \`predev\` script in \`package.json\` runs \`prebuild.js\` before every \`pnpm dev\`\n2. Prebuild reads your spec and generates type-safe code\n3. You import and use it:\n\n\`\`\`typescript\nimport { ${(specName ?? 'api').charAt(0).toUpperCase() + (specName ?? 'api').slice(1)}Client } from '@/generated/${specName}/client';\nimport type { Equipment } from '@/generated/${specName}/types';\n\nconst client = new ${(specName ?? 'api').charAt(0).toUpperCase() + (specName ?? 'api').slice(1)}Client();\nconst gear: Equipment = await client.getEquipment('123');\n\`\`\`\n\nNo manual typing. No drift. No runtime surprises.\n\n`
    : '';

  // Build mock server section if enabled
  const mockSection = hasMock
    ? `## 🎭 Mock Server\n\nPrism mock server is configured to serve your API at **http://localhost:4010**.\n\n\`\`\`bash\npnpm dev:mock    # Starts Prism + Next.js together\n\`\`\`\n\nThis starts:\n1. Prism mock server on port 4010 (serves your OpenAPI spec)\n2. Next.js dev server on port 3000\n\nThe generated API client is automatically configured to use the mock server.\n\n`
    : '';

  // Build AI Agents section if otters are present
  const aiAgentsSection = hasOtters
    ? `## 🦦 AI Agents (The Otter Raft)\n\nYour project includes **SIX specialized AI agents** in \`node_modules/@stackwright-pro/otters/\`:\n\n- \`stackwright-pro-foreman-otter\` - Master coordinator that orchestrates all specialist otters\n- \`stackwright-pro-api-otter\` - Discovers entities from OpenAPI specs\n- \`stackwright-pro-auth-otter\` - Configures CAC/OIDC auth, RBAC rules, and audit middleware\n- \`stackwright-pro-data-otter\` - Configures endpoint filters & ISR revalidation\n- \`stackwright-pro-page-otter\` - Generates pages auto-wired with data, theme, and auth\n- \`stackwright-pro-dashboard-otter\` - Builds live data dashboards\n\n**Start the otter raft:**\n\n\`\`\`bash\nnpx @stackwright-pro/raft\n\`\`\`\n\nThe raft verifies otter integrity, writes project context, and spawns code-puppy in foreman mode. The foreman asks questions about your brand, auth, and data preferences, then coordinates specialist otters to build your app. All state lives in \`.stackwright/\` — interrupt and resume anytime.\n\n**Example session prompt:**\n\n"Build me a logistics dashboard from our OpenAPI spec. Connect to /equipment and /supplies endpoints."\n\n`
    : '';

  return `# ${projectName}\n\nA Stackwright Pro application with role-based auth, OpenAPI integration, and intelligent AI agents.\n\n## 🚀 Quick Start\n\n\`\`\`bash\n# Start development server\npnpm dev\n\n# Open your browser\nopen http://localhost:3000\n\`\`\`\n\n## 👥 Role-Based Development\n\nStackwright Pro includes **mock authentication** for local development with three pre-configured roles:\n\n\`\`\`bash\npnpm dev:admin     # Full permissions (*)\npnpm dev:analyst   # Read & write data\npnpm dev:viewer    # Read-only access\n\`\`\`\n\n**How it works:**\n- Each script sets the \`MOCK_USER\` environment variable\n- \`lib/mock-auth.ts\` intercepts requests and injects the appropriate user context\n- Your pages & API routes respect the RBAC rules in \`stackwright.yml\`\n- No backend required for testing auth flows! 🎉\n\n**Pro tip:** Test your UI for all three roles to ensure proper permissions enforcement.\n\n${mockSection}## 📁 Project Structure\n\n\`\`\`\n.\n├── app/\n│   ├── layout.tsx             # Root layout (StackwrightLayout + Providers)\n│   ├── page.tsx               # Home page (renders root content)\n│   ├── [...slug]/\n│   │   └── page.tsx           # Dynamic pages from YAML content\n│   └── _components/\n│       ├── providers.tsx      # Pro: AuthProvider + component registration\n│       └── page-client.tsx    # Client-side page renderer\n├── pages/\n│   └── content.yml            # Root page content in YAML\n├── lib/\n│   └── mock-auth.ts           # Dev-only auth mocking (no backend needed)\n├── scripts/\n│   └── prebuild.js            # Auto-generates code from OpenAPI specs\n├── specs/                     # OpenAPI spec files (if --spec was used)\n├── src/\n│   └── generated/             # Auto-generated types & clients\n│       └── {specName}/\n│           ├── schemas.ts     # Zod schemas for runtime validation\n│           ├── types.ts       # TypeScript types\n│           ├── client.ts      # API client with auth\n│           └── provider.ts    # CollectionProvider for Stackwright\n├── .stackwright/\n│   └── otters/                # AI agent configs (4 specialized otters)\n├── stackwright.yml            # Theme, auth, API integrations\n└── package.json\n\`\`\`\n\n**Key Files:**\n- \`app/_components/providers.tsx\` - Wraps your app with \`AuthProvider\` for RBAC\n- \`stackwright.yml\` - Single source of truth for theme, auth roles, and API wiring\n- \`lib/mock-auth.ts\` - Mocks CAC/PIV headers locally so you can test auth flows\n- \`scripts/prebuild.js\` - Runs before dev/build to generate types from your OpenAPI spec\n\n## 📄 Adding Pages\n\nStackwright uses **YAML for content**, so you never touch JSX unless you want to.\n\n**Example:** Create \`pages/about/content.yml\`:\n\n\`\`\`yaml\ntitle: About Us\ndescription: Learn more about our mission\n\nsections:\n  - type: main\n    heading: Who We Are\n    body: |\n      We build mission-critical logistics systems for the Marine Corps.\n\n  - type: feature_list\n    title: Our Capabilities\n    features:\n      - icon: Security\n        title: Zero Trust\n        description: PKI-based auth with CAC/PIV\n      - icon: Api\n        title: Real-time Data\n        description: OpenAPI-backed, Zod-validated\n\`\`\`\n\nThat's it. Navigate to \`/about\` and it just works. No routing config, no React components.\n\n${apiIntegrationSection}## 🎨 Customizing Theme\n\nEdit \`stackwright.yml\` to change colors, fonts, spacing — applies everywhere instantly.\n\n\`\`\`yaml\ntheme:\n  id: my-theme\n  name: My Custom Theme\n  colors:\n    primary: "#C41E3A"        # Marine Corps red\n    background: "#FFFFFF"\n    text: "#1A1A1A"\n    accent: "#FFD700"         # Gold\n  typography:\n    fontFamily: "'Inter', sans-serif"\n    h1Size: "2.5rem"\n  spacing:\n    unit: 8\n\`\`\`\n\nNo CSS files. No theme provider boilerplate. Just YAML. Stackwright handles the rest.\n\n## 🔐 Auth Configuration\n\nStackwright Pro comes with **3 pre-configured roles** in \`stackwright.yml\`:\n\n\`\`\`yaml\nauth:\n  roles:\n    - name: ADMIN\n      permissions: ['*']                        # Full access\n    - name: ANALYST  \n      permissions: ['data:read', 'data:write']  # Read/write data\n    - name: VIEWER\n      permissions: ['data:read']                # Read-only\n\`\`\`\n\n**Gate content in your YAML:**\n\n\`\`\`yaml\nsections:\n  - type: button\n    label: Delete Equipment\n    action: /api/equipment/delete\n    auth:\n      required_roles: [ADMIN]\n      fallback: hide              # Options: hide, disable, show_message\n\`\`\`\n\nOnly admins see the button. Analysts and viewers? Button doesn't exist in the DOM.\n\n**Route protection:**\n\n\`\`\`yaml\nauth:\n  protected_routes:\n    - path: /admin/*\n      roles: [ADMIN]\n    - path: /equipment/*  \n      roles: [ANALYST, ADMIN]\n  public_routes:\n    - /\n    - /about\n\`\`\`\n\nNo middleware to write. No useAuth hooks to remember. RBAC is declarative.\n\n${aiAgentsSection}## 📚 Learn More\n\n- **Stackwright Docs:** [https://stackwright.dev](https://stackwright.dev)\n- **OSS Repo:** [https://github.com/Per-Aspera-LLC/stackwright](https://github.com/Per-Aspera-LLC/stackwright)\n- **Pro Repo:** [https://github.com/Per-Aspera-LLC/stackwright-pro](https://github.com/Per-Aspera-LLC/stackwright-pro)\n\n**Questions?** File an issue or ping us in the discussions. We're here to help you ship faster.\n`;
}
